import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import cn from 'classnames';
import { isEmpty } from 'lodash-es';
import { FC } from 'react';
import { generatePath, useNavigate, useSearchParams } from 'react-router';

import { SharingInvitation } from '@/shared/api/protocol-ts/model/dto_sharing_pb';
import { PATHS, SortByFunctionType } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';

import { accessModel } from '@/entities/access';

import { TableWithInfiniteScroll } from '@/widgets/TableWithInfiniteScroll';

import { EmptyResult } from '../EmptyResult/EmptyResult';
import { ListLoadingFailed } from '../ListLoadingFailed/ListLoadingFailed';

import styles from './SharedPatientList.module.scss';

type SharedPatientListProps = {
  sharedPatientColumnsScheme: ColumnDef<SharingInvitation, unknown>[];
  sharedPatientCounter: number;
  reFetchPatients: (id: string) => void;
  sortBy: SortByFunctionType;
  showSharedByColumn?: boolean;
  className?: string;
  testID?: string;
  emptyText?: string;
};

export const SharedPatientList: FC<SharedPatientListProps> = (props) => {
  const {
    sharedPatientColumnsScheme,
    sharedPatientCounter,
    reFetchPatients,
    sortBy,
    showSharedByColumn,
    className,
    testID,
    emptyText,
  } = props;

  const invitationsList = useAppSelector(
    accessModel.sharingSelectors.selectAll,
  );

  const accessListLoading = useAppSelector(
    accessModel.sharingSelectors.selectSharingLoading,
  );

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const search = searchParams.get('search');

  const queryClient = new QueryClient();

  const isListLoadingFailed = accessListLoading === 'failed';

  const showSkeleton =
    (accessListLoading === 'pending' || accessListLoading === 'idle') &&
    invitationsList.length === 0 &&
    sharedPatientCounter > 0;

  const showNothingFound =
    accessListLoading === 'succeeded' &&
    invitationsList.length === 0 &&
    Boolean(search);

  const showEmpty =
    (accessListLoading === 'succeeded' || sharedPatientCounter === 0) &&
    invitationsList.length === 0 &&
    isEmpty(search);

  const getPatientUrl = (patientID: string) => {
    return generatePath(PATHS.patientProfile_DEPRECATED, { patientID });
  };

  const navigateToPatientProfile = (patientID: string) => {
    navigate({ pathname: getPatientUrl(patientID) });
  };

  const navigateToOrganizationInvite = (token: string) => {
    navigate({
      pathname: generatePath(PATHS.organization_invite, { token }),
    });
  };

  return (
    <div
      className={cn(styles.container, className)}
      data-testid={testID}
      data-component="shared-patient-list"
    >
      {isListLoadingFailed && <ListLoadingFailed />}

      {/* TODO: [1/l] may need to move the QueryClientProvider higher up in the routing */}
      {!isListLoadingFailed && (
        <QueryClientProvider client={queryClient}>
          <TableWithInfiniteScroll
            showSharedByColumn={showSharedByColumn}
            listData={invitationsList}
            reFetchList={reFetchPatients}
            itemCounter={sharedPatientCounter}
            clickOnRow={navigateToPatientProfile}
            getRowUrl={getPatientUrl}
            conditionalAdditionalClickOnRow={navigateToOrganizationInvite}
            columnsScheme={sharedPatientColumnsScheme}
            loadingState="access"
            sortBy={sortBy}
            showSkeleton={showSkeleton}
            showNothingFound={showNothingFound}
            showEmpty={showEmpty}
            emptyComponent={<EmptyResult emptyText={emptyText} />}
            serverSideSorting
            className={styles.table}
          />
        </QueryClientProvider>
      )}
    </div>
  );
};
